import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { GuiMsjService } from 'src/app/services/shared/gui-msj.service';
import { Catalogo } from '../../../../../interfaces/interface-bakend/shared/catalogo/catalogo.interface';
import { CatalogoService } from '../../../../../services/shared/catalogo.service';
import { SignoLocalidadService } from 'src/app/services/entidades/signo-localidades.service'; 
import { SignoLocalidad } from 'src/app/interfaces/interface-bakend/entidades/signo-localidad.interface'; 
import Swal from 'sweetalert2';
import { EntidadesKeys } from 'src/app/enum/entidades-keys.enum';

@Component({
    selector: 'app-zona-modificacion',
    templateUrl: './zona-modificacion.component.html',
    styleUrls: ['./zona-modificacion.component.scss']
})
export class ZonaModificacionComponent implements OnInit {
    form: FormGroup;
    catalogo: Catalogo;
    localidades: SignoLocalidad[] = []; 
    localidadesOrdenadas: SignoLocalidad[] = [];
    localidadesSeleccionadas: Set<string> = new Set(); 
    zonas: Catalogo[] = [];

    constructor(
        private router: Router,
        private catalogoService: CatalogoService,
        private signoLocalidadService: SignoLocalidadService, 
        public guiMsjService: GuiMsjService
    ) {}

    async ngOnInit(): Promise<void> {
        this.catalogo = this.catalogoService.getConceptoSeleccionado();
        if (!this.catalogo) {
            await this.router.navigateByUrl('/zonas-listado');
            return;
        }
        this.createForm();
        await this.obtenerDatosIniciales();
    }

    get zonaNoValida() {
        return this.form.get('zona').invalid && this.form.get('zona').touched;
    }

    async obtenerDatosIniciales() {
        try {
            this.zonas = await this.catalogoService.findDescendantsByKey(EntidadesKeys.ZONAS);
            this.localidades = await this.signoLocalidadService.getLocalidades();
            this.ordenarLocalidades();
            this.marcarLocalidadesAsignadas();
        } catch (error) {
            console.error('Error al obtener datos:', error);
        }
    }

    getNombreZona(localidad: SignoLocalidad): string {
        if (!localidad.zona) return '';
        
        const zonaId = typeof localidad.zona === 'string' 
            ? localidad.zona 
            : localidad.zona._id;
            
        const zona = this.zonas.find(z => z._id === zonaId);
        return zona ? zona.concepto : '';
    }

    esLocalidadDeEstaZona(localidad: SignoLocalidad): boolean {
        if (!localidad.zona) return false;
        
        const zonaId = typeof localidad.zona === 'string' 
            ? localidad.zona 
            : localidad.zona._id;
            
        return zonaId === this.catalogo._id;
    }

    ordenarLocalidades() {
        this.localidadesOrdenadas = [...this.localidades].sort((a, b) => {
            const zonaA = this.getNombreZona(a);
            const zonaB = this.getNombreZona(b);
            
            if ((zonaA && zonaB) || (!zonaA && !zonaB)) {
                return a.nombre.localeCompare(b.nombre);
            }
            return zonaA ? -1 : 1;
        });
    }

    marcarLocalidadesAsignadas() {
        this.localidades.forEach(localidad => {
            if (this.esLocalidadDeEstaZona(localidad)) {
                this.localidadesSeleccionadas.add(localidad._id);
            }
        });
    }

    onLocalidadSeleccionada(event: any, localidadId: string) {
        event.checked 
            ? this.localidadesSeleccionadas.add(localidadId)
            : this.localidadesSeleccionadas.delete(localidadId);
    }

    createForm() {
        this.form = new FormGroup({
            zona: new FormControl(this.catalogo?.concepto, [Validators.required, Validators.minLength(3)]),
            activa: new FormControl(this.catalogo?.logico_1 || false),
        });
    }

    async setDataForma(): Promise<Catalogo> {
        return {
            key: null,
            concepto: this.form.value.zona,
            logico_1: this.form.value.activa,
            parent: this.catalogo.parent,
            properties: {},
        };
    }

    async asignarZonaALocalidades(zonaId: string, localidadesIds: string[], esRemover: boolean = false) {
        if (localidadesIds.length === 0) return;
        
        const promesas = localidadesIds.map(async localidadId => {
            const localidad = this.localidades.find(l => l._id === localidadId);
            if (!localidad) return;
            
            const datos: any = {
                nombre: localidad.nombre,
                activo: localidad.activo,
                zona: esRemover ? null : { _id: zonaId }
            };
            
            if (localidad.provincia) {
                datos.provincia = typeof localidad.provincia === 'string'
                    ? { _id: localidad.provincia }
                    : localidad.provincia;
            }
            
            return this.signoLocalidadService.updateLocalidad(localidadId, datos)
                .catch(err => console.error(`Error al actualizar ${localidad.nombre}:`, err));
        });
        
        await Promise.all(promesas);
    }

    async updateZona() {
        await this.guiMsjService.msjFormSubmit('Guardando');
        
        try {
            await this.catalogoService.put(this.catalogo._id, await this.setDataForma());
            
            const localidadesActuales = await this.signoLocalidadService.listarLocalidadesByZona(this.catalogo._id);
            const idsActuales = localidadesActuales.map(l => l._id);
            const idsSeleccionadas = Array.from(this.localidadesSeleccionadas);
            
            const idsARemover = idsActuales.filter(id => !idsSeleccionadas.includes(id));
            const idsAAsignar = idsSeleccionadas.filter(id => !idsActuales.includes(id));
            
            console.log(`Zona ${this.catalogo.concepto}: Remover ${idsARemover.length}, Asignar ${idsAAsignar.length}`);
            
            if (idsARemover.length > 0) {
                await this.asignarZonaALocalidades(this.catalogo._id, idsARemover, true);
            }
            
            if (idsAAsignar.length > 0) {
                await this.asignarZonaALocalidades(this.catalogo._id, idsAAsignar, false);
            }
            
            Swal.fire({
                icon: 'success',
                title: 'Zona modificada satisfactoriamente',
                showConfirmButton: false,
                timer: 1100,
            });
            this.router.navigateByUrl('/zonas-listado');
            
        } catch (error) {
            console.error('Error en updateZona:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al modificar zona',
            });
        }
    }
}